import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import classes from './NoteContent.module.css';

interface Props {
  /** Note body. Either Tiptap-emitted HTML or a legacy plain-text string. */
  content: string;
  className?: string;
}

// Limited to the tags `NoteEditor` is configured to emit. Anything else (which
// can only show up in legacy notes saved before this component existed) is
// stripped by DOMPurify and the surrounding text is preserved.
const ALLOWED_TAGS = ['p', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li', 'br'];

export const NoteContent = ({ content, className }: Props) => {
  // Memoized so a list of notes doesn't re-sanitize every entry on each
  // unrelated parent re-render (real-time subscription updates, etc).
  const sanitized = useMemo(
    () =>
      DOMPurify.sanitize(content, {
        ALLOWED_TAGS,
        ALLOWED_ATTR: [],
        KEEP_CONTENT: true,
      }),
    [content],
  );
  // `dangerouslySetInnerHTML` is safe here: DOMPurify ran above with a strict
  // allow-list and no permitted attributes, so anything that survives is
  // structural markup the editor itself emits — no scripts, no event handlers,
  // no urls. The wrapping `pre-wrap` keeps embedded `\n`s in legacy plain-text
  // notes visible.
  return (
    <div
      className={className ? `${classes.root} ${className}` : classes.root}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};
